import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface GanttTask {
  id: string;
  name: string;
  start: Date;
  end: Date;
  progress: number;
  status: 'pending' | 'in_progress' | 'completed' | 'overdue';
}

interface GanttChartProps {
  tasks: GanttTask[];
  title?: string;
  height?: number;
}

const GanttChart: React.FC<GanttChartProps> = ({
  tasks,
  title = 'Project Timeline',
  height = 400
}) => {
  // Sort tasks by start date
  const sortedTasks = [...tasks].sort((a, b) => a.start.getTime() - b.start.getTime());



  // Create labels (task names)
  const labels = sortedTasks.map(task => task.name);

  // Create datasets for different time periods
  const datasets = [
    {
      label: 'Completed',
      data: sortedTasks.map(task => {
        const completedDuration = (task.end.getTime() - task.start.getTime()) * (task.progress / 100);
        return completedDuration / (1000 * 60 * 60 * 24); // Convert to days
      }),
      backgroundColor: 'rgba(34, 197, 94, 0.8)',
      borderColor: 'rgb(34, 197, 94)',
      borderWidth: 1,
    },
    {
      label: 'Remaining',
      data: sortedTasks.map(task => {
        const remainingDuration = (task.end.getTime() - task.start.getTime()) * ((100 - task.progress) / 100);
        return remainingDuration / (1000 * 60 * 60 * 24); // Convert to days
      }),
      backgroundColor: (task: any) => {
        const taskData = sortedTasks[task.dataIndex];
        if (taskData?.status === 'overdue') return 'rgba(239, 68, 68, 0.8)';
        if (taskData?.status === 'in_progress') return 'rgba(251, 191, 36, 0.8)';
        return 'rgba(156, 163, 175, 0.8)';
      },
      borderColor: (task: any) => {
        const taskData = sortedTasks[task.dataIndex];
        if (taskData?.status === 'overdue') return 'rgb(239, 68, 68)';
        if (taskData?.status === 'in_progress') return 'rgb(251, 191, 36)';
        return 'rgb(156, 163, 175)';
      },
      borderWidth: 1,
    }
  ];

  const data = {
    labels,
    datasets,
  };

  const options = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: title,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const task = sortedTasks[context.dataIndex];
            if (!task?.start || !task?.end) return context.dataset.label;
            const duration = Math.ceil((task.end.getTime() - task.start.getTime()) / (1000 * 60 * 60 * 24));
            return `${context.dataset.label}: ${duration} days (${task.progress || 0}% complete)`;
          }
        }
      }
    },
    scales: {
      x: {
        stacked: true,
        title: {
          display: true,
          text: 'Duration (Days)'
        }
      },
      y: {
        stacked: true,
        title: {
          display: true,
          text: 'Tasks'
        }
      }
    },
  };

  return (
    <div style={{ height: `${height}px` }}>
      <Bar data={data} options={options} />
    </div>
  );
};

export default GanttChart;